/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatDateTime } from '../../utils/timeFormat';

const formatWhen = (value) => formatDateTime(value) || '—';

/**
 * Delivery log for the business-office billing requests.
 *
 * A billing request is sent unattended, whenever the last payer happens to sign,
 * so nobody is watching when it fails. This is where an admin finds out, and
 * where a failure can be resent once the cause is fixed.
 */
const BusinessOfficeEmailDeliveries = () => {
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);
  const [scope, setScope] = useState('failed');
  const [deliveries, setDeliveries] = useState([]);
  const [failedCount, setFailedCount] = useState(0);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/business-office-email/deliveries', {
        params: { status: scope }
      });
      setDeliveries(res.data.deliveries || []);
      setFailedCount(res.data.failed_count || 0);
    } catch (error) {
      console.error('Error loading delivery log:', error);
      toast.error('Failed to load the delivery log');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const retry = async (delivery) => {
    const key = delivery ? delivery.delivery_id : 'all';
    setRetrying(key);
    try {
      const res = await axios.post('/api/business-office-email/deliveries/retry', {
        invoice_id: delivery.invoice_id,
        delivery_id: delivery.delivery_id
      });
      if (res.data.failed > 0) toast.warning(res.data.message);
      else toast.success(res.data.message);
      await fetchDeliveries();
    } catch (error) {
      console.error('Error retrying billing request:', error);
      toast.error(error.response?.data?.message || 'Failed to resend the billing request');
    } finally {
      setRetrying(null);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Delivery log
        </Typography>
        {failedCount > 0 && (
          <Chip size="small" color="error" label={`${failedCount} failed`} />
        )}
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={scope}
          onChange={(e, value) => value && setScope(value)}
        >
          <ToggleButton value="failed">Failures</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <Tooltip title="Refresh">
          <span>
            <Button size="small" onClick={fetchDeliveries} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Billing requests are emailed on their own, whenever the last payer signs. Anything that did
        not reach the business office shows up here so it can be fixed and resent.
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={28} />
        </Box>
      ) : deliveries.length === 0 ? (
        <Alert severity={scope === 'failed' ? 'success' : 'info'}>
          {scope === 'failed'
            ? 'Every billing request has reached the business office.'
            : 'No billing requests have been sent yet.'}
        </Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Invoice</TableCell>
                <TableCell>Payer</TableCell>
                <TableCell>Sent to</TableCell>
                <TableCell>Last attempt</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveries.map(d => (
                <TableRow key={d.delivery_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {d.invoice_id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.project_title || d.project_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{d.payer_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.customer_type}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(d.to || []).join(', ') || <em>no recipient</em>}
                    </Typography>
                    {(d.attachments || []).length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {d.attachments.length} attachment
                        {d.attachments.length === 1 ? '' : 's'}:{' '}
                        {d.attachments.join(', ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatWhen(d.sent_at)}</Typography>
                    {d.attempts > 1 && (
                      <Typography variant="caption" color="text.secondary">
                        {d.attempts} attempts
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={d.status === 'sent' ? 'success' : 'error'}
                      label={d.status === 'sent' ? 'Sent' : 'Failed'}
                    />
                    {d.status === 'failed' && d.error && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ display: 'block', maxWidth: 320, mt: 0.5 }}
                      >
                        {d.error}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {d.status === 'failed' && (
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={retrying !== null}
                        onClick={() => retry(d)}
                      >
                        {retrying === d.delivery_id ? 'Resending...' : 'Resend'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default BusinessOfficeEmailDeliveries;
